import {
  parseAmount,
  sectionInsuranceAmount,
  sectionInsuranceRate,
  effectiveInsurancePctOnTotal,
  analysisInsuranceMode,
  profitTypeLabel,
} from './analysisCalculations';
import type { AnalysisItem, AnalysisSection } from './api/projectAnalysis';

export interface WaterfallRow {
  key: string;
  label: string;
  amount: number;
  tone: 'base' | 'add' | 'minus' | 'total';
}

export interface SectionShareRow {
  id: number;
  name: string;
  base: number;
  insurance: number;
  calculated: number;
  share: number;
  items: number;
  insurancePct: number;
}

export interface SensitivityRow {
  coefficient: number;
  bid: number;
  impliedProfit: number;
  extra: number;
  netProfit: number;
  margin: number;
  viable: boolean;
}

export interface AnalysisInsight {
  base: number;
  insurance: number;
  profit: number;
  tax: number;
  total: number;
  insurancePct: number;
  taxPct: number;
  estimate: number;
  coefficient: number | null;
  difference: number | null;
  differencePct: number | null;
  extraTotal: number;
  netProfit: number;
  netMargin: number;
  grossMargin: number;
  markup: number;
  adjustmentPct: number;
  adjustmentAmount: number;
  totalWithAdjustment: number;
  profitWithAdjustment: number;
  netProfitWithAdjustment: number;
  prepaymentTotal: number;
  maintenanceRetention: number;
  breakEvenCoefficient: number | null;
  coverCoefficient: number | null;
  recommendedCoefficient: number | null;
  profitType: string;
  profitTypeLabel: string;
  waterfall: WaterfallRow[];
  sections: SectionShareRow[];
  sensitivity: SensitivityRow[];
  alerts: Array<{ tone: 'danger' | 'warning' | 'info'; title: string; text: string }>;
}

const extraAmount = (analysis: AnalysisItem, total: number): number =>
  (analysis.extra_coefficients ?? []).reduce((sum, c) => {
    const pct = typeof c.percentage === 'number' ? c.percentage : parseFloat(String(c.percentage)) || 0;
    return sum + Math.ceil((total * pct) / 100);
  }, 0);

export function buildAnalysisInsight(analysis: AnalysisItem): AnalysisInsight {
  const base = parseAmount(analysis.total_base_cost);
  const tax = parseAmount(analysis.total_tax);
  const insurance = parseAmount(analysis.total_insurance);
  const profit = parseAmount(analysis.total_profit);
  const total = parseAmount(analysis.total_cost);
  const estimate = parseAmount(analysis.initial_estimate ?? analysis.project?.initial_estimate);
  const insurancePct = effectiveInsurancePctOnTotal(total, insurance);
  const taxPct =
    analysis.tax_percentage != null && analysis.tax_percentage !== ''
      ? parseAmount(analysis.tax_percentage)
      : total > 0
        ? (tax / total) * 100
        : 0;
  const extraTotal = extraAmount(analysis, total);
  const netProfit = profit - extraTotal;
  const coefficient = estimate > 0 && total > 0 ? total / estimate : null;
  const difference = estimate > 0 ? total - estimate : null;
  const differencePct = estimate > 0 && coefficient != null ? (coefficient - 1) * 100 : null;
  const grossMargin = total > 0 ? (profit / total) * 100 : 0;
  const netMargin = total > 0 ? (netProfit / total) * 100 : 0;
  const markup = base > 0 ? ((total - base) / base) * 100 : 0;

  const adjustmentPct =
    analysis.has_adjustment && analysis.adjustment_predicted_percentage != null
      ? parseFloat(String(analysis.adjustment_predicted_percentage)) || 0
      : 0;
  const adjustmentAmount = Math.ceil(total * (adjustmentPct / 100));
  const totalWithAdjustment = total + adjustmentAmount;
  const taxOnAdjustment = Math.ceil(adjustmentAmount * (taxPct / 100));
  const insuranceOnAdjustment = Math.ceil(adjustmentAmount * (insurancePct / 100));
  const profitWithAdjustment = Math.ceil(profit + adjustmentAmount - taxOnAdjustment - insuranceOnAdjustment);
  const extraOnAdjusted = extraAmount(analysis, totalWithAdjustment);
  const netProfitWithAdjustment = profitWithAdjustment - extraOnAdjusted;

  const prepaymentTotalPct = analysis.prepayment_total_percent != null ? parseFloat(String(analysis.prepayment_total_percent)) || 0 : 0;
  const prepaymentShares = analysis.has_prepayment && analysis.prepayment_percentages?.length
    ? analysis.prepayment_percentages.slice(0, analysis.prepayment_installments ?? 0).map((p) => parseFloat(String(p)) || 0)
    : [];
  const prepaymentTotal = prepaymentTotalPct > 0
    ? (total * prepaymentTotalPct) / 100
    : prepaymentShares.reduce((sum, pct) => sum + (total * pct) / 100, 0);

  const maintenanceRetention = analysis.has_maintenance_period && analysis.maintenance_retention_percentage != null
    ? (total * (parseFloat(String(analysis.maintenance_retention_percentage)) || 0)) / 100
    : 0;

  const taxFactor = taxPct >= 100 ? 1 : 1 - taxPct / 100;
  const insRate = insurancePct / 100;
  const combinedFactor = taxFactor - insRate;
  const zeroProfitTotal = combinedFactor > 0.0001 ? base / combinedFactor : base + insurance;
  const breakEvenCoefficient = estimate > 0 ? zeroProfitTotal / estimate : null;
  const coverCoefficient = estimate > 0 ? zeroProfitTotal / estimate : null;
  const recommendedCoefficient = estimate > 0 && total > 0 ? total / estimate : null;

  const extraRate = total > 0 ? extraTotal / total : 0;
  const sensitivity: SensitivityRow[] = estimate > 0
    ? [0.8, 0.85, 0.9, 0.95, 1, 1.05, 1.1, 1.15, 1.2].map((coeff) => {
        const bid = estimate * coeff;
        const impliedProfit = combinedFactor > 0.0001 ? bid * combinedFactor - base : bid * taxFactor - base - insurance;
        const extra = bid * extraRate;
        const net = impliedProfit - extra;
        return {
          coefficient: coeff,
          bid,
          impliedProfit,
          extra,
          netProfit: net,
          margin: bid > 0 ? (net / bid) * 100 : 0,
          viable: net >= 0,
        };
      })
    : [];

  const sections: SectionShareRow[] = (analysis.sections ?? []).map((section: AnalysisSection) => {
    const sectionBase = parseAmount(section.base_cost);
    return {
      id: section.id,
      name: section.name,
      base: sectionBase,
      insurance: sectionInsuranceAmount(section, analysis),
      calculated: parseAmount(section.calculated_cost),
      share: base > 0 ? (sectionBase / base) * 100 : 0,
      items: section.items?.length ?? 0,
      insurancePct: sectionInsuranceRate(section, analysis),
    };
  }).sort((a, b) => b.calculated - a.calculated);

  const alerts: AnalysisInsight['alerts'] = [];
  if (profit < 0 || netProfit < 0) {
    alerts.push({
      tone: 'danger',
      title: 'زیان‌ده',
      text: 'سود خالص منفی است. قیمت پیشنهادی هزینه‌ها، بیمه، مالیات و کسورات را پوشش نمی‌دهد.',
    });
  }
  if (coefficient != null && coefficient < 1) {
    alerts.push({
      tone: 'warning',
      title: 'ضریب زیر یک',
      text: 'قیمت پیشنهادی از برآورد اولیه کارفرما کمتر است؛ در مناقصه رقابتی است اما حاشیه سود را کنترل کنید.',
    });
  }
  if (estimate <= 0) {
    alerts.push({
      tone: 'info',
      title: 'برآورد اولیه ندارد',
      text: 'برای محاسبه ضریب پیمانکار و تحلیل حساسیت، برآورد اولیه کارفرما را وارد کنید.',
    });
  }
  if (insurance <= 0) {
    alerts.push({
      tone: 'info',
      title: 'بیمه صفر',
      text: analysisInsuranceMode(analysis) === 'global'
        ? 'درصد بیمه کلی در تنظیمات صفر است. اگر قرارداد مشمول بیمه است، درصد را در تنظیمات وارد کنید.'
        : 'هیچ بخشی درصد بیمه ندارد. اگر قرارداد مشمول بیمه است، درصد را روی بخش‌ها یا به‌صورت کلی در تنظیمات وارد کنید.',
    });
  }
  if ((analysis.sections?.length ?? 0) === 0) {
    alerts.push({
      tone: 'warning',
      title: 'بدون بخش',
      text: 'هنوز بخشی برای متره ثبت نشده است.',
    });
  }

  const waterfall: WaterfallRow[] = [
    { key: 'base', label: 'هزینه پایه', amount: base, tone: 'base' },
    { key: 'insurance', label: `بیمه (${insurancePct.toFixed(2)}٪)`, amount: insurance, tone: 'add' },
    { key: 'profit', label: profit >= 0 ? 'سود' : 'زیان', amount: profit, tone: profit >= 0 ? 'add' : 'minus' },
    { key: 'tax', label: `مالیات (${taxPct.toFixed(2)}٪)`, amount: tax, tone: 'add' },
    { key: 'extra', label: 'کسورات اضافی', amount: extraTotal, tone: 'minus' },
    { key: 'total', label: 'قیمت پیشنهادی', amount: total, tone: 'total' },
  ];

  return {
    base,
    insurance,
    profit,
    tax,
    total,
    insurancePct,
    taxPct,
    estimate,
    coefficient,
    difference,
    differencePct,
    extraTotal,
    netProfit,
    netMargin,
    grossMargin,
    markup,
    adjustmentPct,
    adjustmentAmount,
    totalWithAdjustment,
    profitWithAdjustment,
    netProfitWithAdjustment,
    prepaymentTotal,
    maintenanceRetention,
    breakEvenCoefficient,
    coverCoefficient,
    recommendedCoefficient,
    profitType: analysis.profit_type ?? 'percentage_gross',
    profitTypeLabel: profitTypeLabel(analysis.profit_type),
    waterfall,
    sections,
    sensitivity,
    alerts,
  };
}
